<template>
  <div class="page-container">
    <div class="page-header">
      <div>
        <div class="page-title">
          {{ isEditMode ? '계정 정보 수정' : '계정 추가' }}
        </div>

        <div class="page-subtitle">
          {{ isEditMode ? '계정 정보를 수정해주세요.' : '계정 추가를 위해 필수 정보를 입력해주세요.' }}
        </div>
      </div>

      <div class="header-buttons">
        <button class="btn ghost" type="button" @click="$emit('back')">
          취소
        </button>
        <button class="btn primary" type="button" @click="onSubmit" :disabled="loading">
          {{ isEditMode ? '수정하기' : '등록하기' }}
        </button>
      </div>
    </div>

    <div class="content-wrapper two-column account-create-two-column">
      <!-- 좌측: 계정 정보 -->
      <div class="card account-panel">
        <div class="account-panel-inner">
          <div class="panel-title">계정 정보</div>

          <div class="form-grid">
            <div class="form-row">
              <div class="form-label required">아이디</div>
              <div class="form-field input-inline-group">
                <input
                  v-model="form.accountId"
                  class="control"
                  type="text"
                  placeholder="아이디를 입력해주세요"
                  :disabled="isEditMode"
                />
                <button
                  v-if="!isEditMode"
                  class="btn secondary sm"
                  type="button"
                  @click="checkDuplicateId"
                >
                  중복확인
                </button>
              </div>
            </div>

            <div class="form-row">
              <div class="form-label" :class="{ required: !isEditMode }">비밀번호</div>
              <div class="form-field">
                <input
                  v-model="form.password"
                  class="control"
                  type="password"
                  :placeholder="isEditMode ? '변경할 때만 입력해주세요' : '비밀번호를 입력해주세요'"
                />
              </div>
            </div>

            <div class="form-row">
              <div class="form-label" :class="{ required: !isEditMode || !!form.password }">
                비밀번호 확인
              </div>
              <div class="form-field">
                <input
                  v-model="form.passwordConfirm"
                  class="control"
                  type="password"
                  placeholder="비밀번호를 다시 입력해주세요"
                />
              </div>
            </div>

            <div class="form-row">
              <div class="form-label required">권한</div>
              <div class="form-field">
                <select v-model="form.userRoleId" class="control select-control">
                  <option :value="null">권한을 선택해주세요</option>
                  <option v-for="role in roleOptions" :key="role.id" :value="role.id">
                    {{ role.roleName }}
                  </option>
                </select>
              </div>
            </div>

            <div class="form-row">
              <div class="form-label">대리점</div>
              <div class="form-field">
                <select v-model="form.agencyId" class="control select-control">
                  <option :value="null">대리점을 선택하지 않음</option>
                  <option v-for="agency in agencyOptions" :key="agency.id" :value="agency.id">
                    {{ agency.name }}
                  </option>
                </select>
              </div>
            </div>

            <div class="form-row">
              <div class="form-label">발주처</div>
              <div class="form-field">
                <select v-model="form.clientId" class="control select-control">
                  <option :value="null">발주처를 선택하지 않음</option>
                  <option v-for="client in clientOptions" :key="client.id" :value="client.id">
                    {{ client.name }}
                  </option>
                </select>
              </div>
            </div>

            <div class="form-row form-row-top">
              <div class="form-label">메모</div>
              <div class="form-field">
                <textarea
                  v-model="form.memo"
                  class="control account-textarea"
                  placeholder="메모를 입력해주세요"
                ></textarea>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 우측: 접근 가능 현장 -->
      <div class="card account-panel">
        <div class="account-panel-inner">
          <div class="panel-title">접근 가능 현장</div>

          <div class="site-check-wrap">
            <div class="site-check-toolbar">
              <label class="site-check-all">
                <input type="checkbox" :checked="isAllSelected" @change="toggleAllSites" />
                <span>전체선택</span>
              </label>

              <span class="site-check-count">
                선택 {{ form.accessibleSiteIds.length }} / 전체 {{ siteOptions.length }}
              </span>
            </div>

            <div class="site-check-list">
              <label v-for="site in siteOptions" :key="site.id" class="site-check-item">
                <input v-model="form.accessibleSiteIds" type="checkbox" :value="site.id" />
                <span class="site-check-label">{{ site.name }}</span>
              </label>

              <div v-if="!siteOptions.length" class="empty-text">
                표시할 현장이 없습니다.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="bottom-actions">
      <button class="btn secondary" type="button" @click="$emit('back')">
        취소
      </button>
      <button class="btn primary" type="button" @click="onSubmit" :disabled="loading">
        {{ isEditMode ? '수정하기' : '등록하기' }}
      </button>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios'

export default {
  name: 'AccountCreate',

  props: {
    mode: {
      type: String,
      default: 'create',
    },
    formValue: {
      type: Object,
      default() {
        return {}
      },
    },
  },

  emits: ['back', 'saved'],

  data() {
    return {
      loading: false,
      isIdChecked: false,

      form: {
        id: null,
        accountId: '',
        password: '',
        passwordConfirm: '',
        userRoleId: null,
        memo: '',
        agencyId: null,
        clientId: null,
        accessibleSiteIds: [],
      },

      roleOptions: [],
      agencyOptions: [],
      clientOptions: [],
      siteOptions: [],

      currentUser: null,
    }
  },

  computed: {
    isEditMode() {
      return this.mode === 'edit'
    },

    isAllSelected() {
      if (!this.siteOptions.length) return false
      return this.form.accessibleSiteIds.length === this.siteOptions.length
    },
  },

  async mounted() {
    await this.fetchCurrentUser()

    await this.fetchRoleOptions()
    await this.fetchAgencyOptions()
    await this.fetchClientOptions()
    await this.fetchSiteOptions()

    if (this.isEditMode && this.formValue?.id) {
      await this.fetchDetail(this.formValue.id)
    } else {
      this.applyFormValue(this.formValue)
    }
  },

  watch: {
    formValue: {
      deep: true,
      async handler(value) {
        if (this.isEditMode && value?.id) {
          await this.fetchDetail(value.id)
        } else {
          this.applyFormValue(value)
        }
      },
    },
  },

  methods: {
    async fetchCurrentUser() {
      try {
        const response = await api.get('/auth/me')
        this.currentUser = response.data || null
      } catch (error) {
        console.error('현재 로그인 사용자 조회 실패', error)
        this.currentUser = null
      }
    },

    getCurrentRoleOrder() {
      const roleCode = this.currentUser?.roleCode

      if (roleCode === 'SUPER_ADMIN') return 1
      if (roleCode === 'ADMIN') return 2
      if (roleCode === 'OPERATOR') return 3
      if (roleCode === 'USER') return 4
      if (roleCode === 'MONITOR') return 5

      return 999
    },

    applyFormValue(value) {
      if (!value) return

      this.form = {
        id: value.id ?? null,
        accountId: value.accountId || '',
        password: '',
        passwordConfirm: '',
        userRoleId: value.userRoleId ?? null,
        memo: value.memo || '',
        agencyId: value.agencyId ?? null,
        clientId: value.clientId ?? null,
        accessibleSiteIds: Array.isArray(value.accessibleSiteIds)
          ? [...value.accessibleSiteIds]
          : [],
      }
    },

    async fetchDetail(id) {
      try {
        this.loading = true

        const response = await api.get('/app/accountUser/detail', {
          params: { id },
        })

        const detail = response.data?.result

        this.form = {
          id: detail?.id ?? null,
          accountId: detail?.accountId ?? '',
          password: '',
          passwordConfirm: '',
          userRoleId: detail?.userRoleId ?? null,
          memo: detail?.memo ?? '',
          agencyId: detail?.agencyId ?? null,
          clientId: detail?.clientId ?? null,
          accessibleSiteIds: Array.isArray(detail?.accessibleSiteIds)
            ? [...detail.accessibleSiteIds]
            : [],
        }
      } catch (error) {
        console.error('계정 상세 조회 실패', error)
        alert('계정 상세 조회에 실패했습니다.')
      } finally {
        this.loading = false
      }
    },

    async fetchRoleOptions() {
      try {
        const response = await api.get('/app/userRole/list')
        const list = Array.isArray(response.data?.result) ? response.data.result : []

        const currentOrder = this.getCurrentRoleOrder()

        this.roleOptions = list.filter(role => {
          const roleOrder = Number(role.sortOrder ?? 999)
          return roleOrder >= currentOrder
        })
      } catch (error) {
        console.error('권한 목록 조회 실패', error)
        this.roleOptions = []
      }
    },

    async fetchAgencyOptions() {
      try {
        const response = await api.get('/app/agency/list', {
          params: {
            page: 0,
            size: 999,
            deleted: false,
            orderBy: 'nameAsc',
          },
        })

        const content = response.data?.result?.content ?? response.data?.result ?? []
        this.agencyOptions = Array.isArray(content) ? content : []
      } catch (error) {
        console.error('대리점 목록 조회 실패', error)
        this.agencyOptions = []
      }
    },

    async fetchClientOptions() {
      try {
        const response = await api.get('/app/client/list', {
          params: {
            page: 0,
            size: 999,
            deleted: false,
            orderBy: 'nameAsc',
          },
        })

        const content = response.data?.result?.content ?? response.data?.result ?? []
        this.clientOptions = Array.isArray(content) ? content : []
      } catch (error) {
        console.error('발주처 목록 조회 실패', error)
        this.clientOptions = []
      }
    },

    async fetchSiteOptions() {
      try {
        const response = await api.get('/app/site/list', {
          params: {
            deleted: false,
            orderBy: 'siteNameAsc',
          },
        })

        const result = response.data?.result ?? []
        this.siteOptions = Array.isArray(result) ? result : []
      } catch (error) {
        console.error('현장 목록 조회 실패', error)
        this.siteOptions = []
      }
    },

    async checkDuplicateId() {
      if (!this.form.accountId?.trim()) {
        alert('아이디를 먼저 입력해주세요.')
        return
      }

      try {
        const response = await api.get('/app/accountUser/checkUsername', {
          params: {
            accountId: this.form.accountId.trim(),
          },
        })

        const result = response.data

        if (result?.duplicated) {
          this.isIdChecked = false
          alert(result?.message || '이미 사용 중인 아이디입니다.')
          return
        }

        this.isIdChecked = true
        alert(result?.message || '사용 가능한 아이디입니다.')
      } catch (error) {
        console.error('아이디 중복확인 실패', error)
        this.isIdChecked = false
        alert('아이디 중복확인에 실패했습니다.')
      }
    },

    toggleAllSites(event) {
      const checked = event.target.checked

      if (checked) {
        this.form.accessibleSiteIds = this.siteOptions.map(site => site.id)
      } else {
        this.form.accessibleSiteIds = []
      }
    },

    validateForm() {
      if (!this.form.accountId?.trim()) {
        alert('아이디를 입력해주세요.')
        return false
      }

      if (!this.isEditMode && !this.isIdChecked) {
        alert('아이디 중복확인을 진행해주세요.')
        return false
      }

      if (!this.isEditMode) {
        if (!this.form.password) {
          alert('비밀번호를 입력해주세요.')
          return false
        }

        if (!this.form.passwordConfirm) {
          alert('비밀번호 확인을 입력해주세요.')
          return false
        }

        if (this.form.password !== this.form.passwordConfirm) {
          alert('비밀번호가 일치하지 않습니다.')
          return false
        }
      }

      if (this.isEditMode && (this.form.password || this.form.passwordConfirm)) {
        if (!this.form.password) {
          alert('변경할 비밀번호를 입력해주세요.')
          return false
        }

        if (!this.form.passwordConfirm) {
          alert('비밀번호 확인을 입력해주세요.')
          return false
        }

        if (this.form.password !== this.form.passwordConfirm) {
          alert('비밀번호가 일치하지 않습니다.')
          return false
        }
      }

      if (!this.form.userRoleId) {
        alert('권한을 선택해주세요.')
        return false
      }

      return true
    },

    async onSubmit() {
      if (!this.validateForm()) return

      try {
        this.loading = true

        if (this.isEditMode) {
          await api.post('/app/accountUser/update', {
            id: this.form.id,
            password: this.form.password?.trim() ? this.form.password : null,
            userRoleId: this.form.userRoleId,
            memo: this.form.memo,
            agencyId: this.form.agencyId,
            clientId: this.form.clientId,
            accessibleSiteIds: this.form.accessibleSiteIds,
          })
        } else {
          await api.post('/app/accountUser/create', {
            accountId: this.form.accountId.trim(),
            password: this.form.password,
            userRoleId: this.form.userRoleId,
            memo: this.form.memo,
            agencyId: this.form.agencyId,
            clientId: this.form.clientId,
            accessibleSiteIds: this.form.accessibleSiteIds,
          })
        }

        alert(this.isEditMode ? '수정되었습니다.' : '등록되었습니다.')
        this.$emit('saved')
      } catch (error) {
        console.error('계정 저장 실패', error)
        alert(error?.response?.data?.message || '저장에 실패했습니다.')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
@import "@/styles/admin/admin-layout.css";
@import "@/styles/admin/admin-button.css";
@import "@/styles/admin/admin-input.css";
@import "@/styles/admin/admin-form.css";
@import "@/styles/admin/admin-card.css";

.account-create-two-column {
  grid-template-columns: minmax(0, 1fr) 420px;
  align-items: start;
}

.account-panel {
  min-width: 0;
}

.account-panel-inner {
  padding: 24px;
}

.account-panel .panel-title {
  margin-bottom: 24px;
}

.form-grid {
  gap: 22px;
}

.form-row {
  margin-bottom: 0;
}

.form-row-top {
  align-items: flex-start;
}

.form-row-top .form-label {
  padding-top: 12px;
}

.account-textarea {
  min-height: 120px;
  height: auto;
  padding: 12px 14px;
  resize: vertical;
  line-height: 1.6;
}

.site-check-wrap {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #f8fafc;
  padding: 18px 20px;
}

.site-check-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 14px;
  margin-bottom: 14px;
  border-bottom: 1px solid #e5e7eb;
}

.site-check-all {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}

.site-check-all input[type='checkbox'] {
  width: 16px;
  height: 16px;
  margin: 0;
}

.site-check-count {
  font-size: 13px;
  color: #6b7280;
}

.site-check-list {
  max-height: 420px;
  overflow-y: auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  padding-right: 4px;
}

.site-check-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
  font-size: 14px;
  color: #374151;
}

.site-check-item input[type='checkbox'] {
  width: 16px;
  height: 16px;
  margin: 2px 0 0 0;
  flex: 0 0 auto;
}

.site-check-label {
  line-height: 1.45;
  word-break: break-word;
}

.bottom-actions {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

@media (max-width: 1280px) {
  .account-create-two-column {
    grid-template-columns: 1fr;
  }

  .site-check-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    max-height: 240px;
    gap: 12px 20px;
  }
}

@media (max-width: 768px) {
  .account-panel-inner {
    padding: 16px;
  }

  .form-row-top .form-label {
    padding-top: 0;
  }

  .site-check-toolbar {
    flex-direction: column;
    align-items: flex-start;
  }

  .site-check-list {
    grid-template-columns: 1fr;
  }

  .bottom-actions {
    justify-content: stretch;
  }

  .bottom-actions .btn {
    flex: 1;
  }
}
</style>